import { Fragment, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import FilePreview from "../Common/FilePreview";

const statusOptions = ["Pending Approval"];

const DocumentUpload = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const signatureRef = useRef(null);
  const userDetails = useSelector((state) => state.auth?.userDetails);
  const profileSignatureUrl = userDetails?.signature ? getFileUrl(userDetails.signature) : null;

  const projectFromUrl = searchParams.get("project") || "";
  const workOrderFromUrl = searchParams.get("workOrder") || "";
  const documentTypeFromUrl = searchParams.get("documentType") || "";

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [loadingWO, setLoadingWO] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [signMode, setSignMode] = useState(profileSignatureUrl ? "profile" : "draw");
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  const [form, setForm] = useState({
    project: "",
    workOrder: "",
    documentType: "",
    file: null,
    remarks: "",
    status: "Pending Approval",
  });

  useEffect(() => {
    fetchProjects();
    fetchWorkOrders();
    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    if (projectFromUrl) {
      setForm((prev) => ({
        ...prev,
        project: projectFromUrl,
      }));
    }
    if (workOrderFromUrl) {
      setForm((prev) => ({
        ...prev,
        workOrder: workOrderFromUrl,
      }));
    }
  }, [projectFromUrl, workOrderFromUrl]);

  useEffect(() => {
    if (!documentTypeFromUrl || !documentTypes.length) return;

    const match = documentTypes.find(
      (d) => d.name === documentTypeFromUrl
    );

    if (match) {
      setForm((prev) => ({
        ...prev,
        documentType: match._id,
      }));
    }
  }, [documentTypeFromUrl, documentTypes]);

  useEffect(() => {
    if (id) {
      setEditMode(true);
      fetchDocument(id);
    }
  }, [id]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get('/projects?limit=500');
      if (response.data.success) {
        setProjects(response.data.data || []);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      setLoadingWO(true);
      const response = await api.get('/work-orders?limit=500');
      if (response.data.success) {
        setWorkOrders(response.data.data || []);
      } else {
        setWorkOrders([]);
      }
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setWorkOrders([]);
    } finally {
      setLoadingWO(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await api.get("/document-types");
      if (response.data.success) {
        setDocumentTypes(response.data.data || []);
      } else {
        setDocumentTypes([]);
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
      setDocumentTypes([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchDocument = async (docId) => {
    try {
      setLoadingDoc(true);
      const response = await api.get(`/documents/${docId}`);
      if (response.data.success) {
        const doc = response.data.data;
        const linkedProjectId = doc.project?._id || doc.project || doc.workOrder?.project?._id || doc.workOrder?.project || "";
        setForm({
          project: linkedProjectId,
          workOrder: doc.workOrder?._id || doc.workOrder || "",
          documentType: doc.documentType?._id || doc.documentType || "",
          file: doc.file || null,
          remarks: doc.remarks || "",
          status: doc.status || "Pending Approval",
        });
      } else {
        swal("Error", "Failed to load document for editing", "error");
        navigate("/documents");
      }
    } catch (error) {
      console.error("Error loading document:", error);
      swal("Error", "Failed to load document for editing", "error");
      navigate("/documents");
    } finally {
      setLoadingDoc(false);
    }
  };

  const handleProjectChange = (e) => {
    const selectedProjectId = e.target.value;
    setForm((prev) => {
      let updatedWO = prev.workOrder;
      if (selectedProjectId && updatedWO) {
        const currentWO = workOrders.find((w) => String(w._id) === String(updatedWO));
        const currentWOProjId = currentWO?.project?._id || currentWO?.project;
        if (currentWOProjId && String(currentWOProjId) !== String(selectedProjectId)) {
          updatedWO = "";
        }
      }
      return {
        ...prev,
        project: selectedProjectId,
        workOrder: updatedWO,
      };
    });
  };

  const handleWorkOrderChange = (e) => {
    const selectedWoId = e.target.value;
    const selectedWO = workOrders.find((w) => String(w._id) === String(selectedWoId));
    const autoProjId = selectedWO?.project?._id || selectedWO?.project || "";

    setForm((prev) => ({
      ...prev,
      workOrder: selectedWoId,
      project: autoProjId ? autoProjId : prev.project,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "status" && value !== "Approved") {
      signatureRef.current?.clear?.();
      setShowSignatureModal(false);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    setForm((prev) => ({ ...prev, file }));
  };

  const buildSignaturePayload = () => {
    if (signMode === "draw" && signatureRef.current) {
      const dataURL = signatureRef.current.toDataURL("image/png");
      const parts = dataURL.split(",");
      const mime = parts[0].match(/:(.*?);/)[1];
      const binary = atob(parts[1]);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new File([new Blob([bytes], { type: mime })], "document-signature.png", { type: "image/png" });
    }

    if (signMode === "profile" && userDetails?.signature) {
      return userDetails.signature;
    }

    return null;
  };

  const submitDocument = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (form.project) formData.append("project", form.project);
      formData.append("workOrder", form.workOrder);
      formData.append("documentType", form.documentType);
      if (form.file) formData.append("file", form.file);
      formData.append("remarks", form.remarks);
      formData.append("status", form.status);

      if (form.status === "Approved") {
        const signaturePayload = buildSignaturePayload();
        if (!signaturePayload) {
          swal("Warning", "Signature is required when status is Approved.", "warning");
          return;
        }

        if (typeof signaturePayload === "string") {
          formData.append("existingSignaturePath", signaturePayload);
        } else {
          formData.append("signature", signaturePayload);
        }
      }

      let response;
      if (editMode) {
        response = await api.patch(`/documents/${id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await api.post("/documents", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (response.data.success) {
        swal({
          title: "Success!",
          text: `Document ${editMode ? "updated" : "uploaded"} successfully!`,
          icon: "success",
          button: "OK",
        }).then(() => navigate("/documents"));
      } else {
        swal("Error", response.data.message || "Operation failed!", "error");
      }
    } catch (error) {
      console.error("Error submitting document:", error);
      swal("Error", error.response?.data?.message || "Failed to submit document.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.workOrder) {
      swal("Validation Error", "Please select a Work Order.", "warning");
      return;
    }

    if (form.status === "Approved") {
      setSignMode(profileSignatureUrl ? "profile" : "draw");
      setShowSignatureModal(true);
      return;
    }

    swal({
      title: editMode ? "Update Document?" : "Upload Document?",
      text: editMode ? "Are you sure you want to update this document?" : "Are you sure you want to upload this document?",
      icon: "warning",
      buttons: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      await submitDocument();
    });
  };

  const filteredWorkOrders = form.project
    ? workOrders.filter((wo) => {
        const pId = wo.project?._id || wo.project;
        return pId && String(pId) === String(form.project);
      })
    : workOrders;

  return (
    <Fragment>
      <PageTitle
        activeMenu={editMode ? "Edit Document" : "Upload Document"}
        motherMenu="Document & Approval Management"
      />
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title>{editMode ? "Edit Document" : "Upload Document"}</Card.Title>
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(
                    workOrderFromUrl || documentTypeFromUrl || projectFromUrl
                      ? `/documents?${projectFromUrl ? `project=${projectFromUrl}&` : ""}${workOrderFromUrl ? `workOrder=${workOrderFromUrl}&` : ""}${documentTypeFromUrl ? `documentType=${documentTypeFromUrl}` : ""}`
                      : "/documents"
                  )
                }
                disabled={loading}
              >
                Back to Documents
              </Button>
            </Card.Header>
            <Card.Body>
              {loadingDoc ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>

                  {/* Project */}
                  <div className="mb-3">
                    <label className="form-label">Project <span className="text-danger">*</span></label>
                    {loadingProjects ? (
                      <div className="text-muted small py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        Loading projects...
                      </div>
                    ) : (
                      <select
                        className="form-select"
                        name="project"
                        value={form.project}
                        onChange={handleProjectChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Project</option>
                        {projects.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.projectCode ? `${p.projectCode} - ${p.projectName}` : (p.projectName || p.name)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Work Order */}
                  <div className="mb-3">
                    <label className="form-label">Work Order <span className="text-danger">*</span></label>
                    {loadingWO ? (
                      <div className="text-muted small py-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        Loading work orders...
                      </div>
                    ) : (
                      <select
                        className="form-select"
                        name="workOrder"
                        value={form.workOrder}
                        onChange={handleWorkOrderChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Work Order</option>
                        {filteredWorkOrders.map((wo) => (
                          <option key={wo._id} value={wo._id}>
                            {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""} {!form.project && wo.project?.projectName ? ` — ${wo.project.projectName}` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Document Type */}
                  <div className="mb-3">
                    <label className="form-label">
                      Document Type <span className="text-danger">*</span>
                    </label>

                    {loadingTypes ? (
                      <div className="text-muted small py-2">Loading document types...</div>
                    ) : (
                      <select
                        className="form-select"
                        name="documentType"
                        value={form.documentType}
                        onChange={handleChange}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Type</option>
                        {documentTypes.map((dt) => (
                          <option key={dt._id} value={dt._id}>
                            {dt.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    <label className="form-label">Status <span className="text-danger">*</span></label>
                    <select
                      className="form-select"
                      name="status"
                      value={form.status}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    >
                      {statusOptions.map((s, i) => (
                        <option key={i} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* File */}
                  <div className="mb-3">
                    <label className="form-label">
                      {editMode ? "Replace File (optional)" : "Upload File"} {!editMode && <span className="text-danger">*</span>}
                    </label>
                    <input
                      type="file"
                      name="file"
                      className="form-control"
                      onChange={handleFile}
                      required={!editMode}
                      disabled={loading}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.dwg,.dxf,.dgn,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif"
                    />
                    <FilePreview 
                      file={form.file} 
                      fileUrl={form.file && typeof form.file === "string" ? getFileUrl(form.file) : null} 
                    />
                  </div>

                  {/* Remarks */}
                  <div className="mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea
                      name="remarks"
                      className="form-control"
                      rows="3"
                      value={form.remarks}
                      onChange={handleChange}
                      disabled={loading}
                      placeholder="Optional remarks"
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <Button type="submit" variant="primary" disabled={loading}>
                    {loading ? "Processing..." : editMode ? "Update" : "Submit"}
                  </Button>
                  <Button
                    variant="danger"
                    className="ms-2"
                    onClick={() => navigate("/documents")}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Signature Modal for Approved status */}
      <Modal show={showSignatureModal} onHide={() => setShowSignatureModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Sign to Approve Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {profileSignatureUrl && (
            <div className="mb-3">
              <label className="form-label d-block fw-bold">Signature Method</label>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="docSignMode"
                  id="docSignModeProfile"
                  value="profile"
                  checked={signMode === "profile"}
                  onChange={() => setSignMode("profile")}
                />
                <label className="form-check-label" htmlFor="docSignModeProfile">
                  Use Profile Signature
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="radio"
                  name="docSignMode"
                  id="docSignModeDraw"
                  value="draw"
                  checked={signMode === "draw"}
                  onChange={() => setSignMode("draw")}
                />
                <label className="form-check-label" htmlFor="docSignModeDraw">
                  Draw Signature
                </label>
              </div>
            </div>
          )}

          {signMode === "profile" && profileSignatureUrl ? (
            <div className="p-3 border rounded text-center bg-light">
              <p className="text-muted small mb-2">Saved Profile Signature:</p>
              <img
                src={profileSignatureUrl}
                alt="Profile Signature"
                style={{ maxHeight: "100px", maxWidth: "100%", objectFit: "contain" }}
              />
            </div>
          ) : (
            <div>
              <p className="text-muted small mb-2">Draw your signature in the box below:</p>
              <div className="border rounded bg-white">
                <SignatureCanvas
                  ref={signatureRef}
                  penColor="black"
                  canvasProps={{
                    className: "w-100",
                    style: { height: "180px", touchAction: "none" }
                  }}
                />
              </div>
              <div className="text-end mt-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => signatureRef.current?.clear()}
                >
                  Clear Signature
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSignatureModal(false)}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={async () => {
              if (signMode === "draw" && signatureRef.current?.isEmpty()) {
                swal("Warning", "Please draw a signature before approving.", "warning");
                return;
              }
              setShowSignatureModal(false);
              await submitDocument();
            }}
          >
            Confirm & Approve
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default DocumentUpload;
